import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { VaultItemPlain } from "@boveda/shared";
import { KeyRound, Wand2 } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { useI18n } from "@/providers/i18n-provider";
import type { DecryptedVaultItem } from "../vault-context";
import { PasswordGenerator } from "./password-generator";

export type ItemFormType = "login" | "note";

interface ItemFormProps {
  /** Item existente a editar, o undefined para crear uno nuevo. */
  editing?: DecryptedVaultItem;
  /** Tipo inicial al crear. */
  initialType?: ItemFormType;
  onSave: (plain: VaultItemPlain, existing?: { id: string; rev: number }) => Promise<void>;
  onCancel: () => void;
}

/** Formulario de creación/edición de item (login o nota). */
export function ItemForm(props: ItemFormProps) {
  const { t } = useI18n();
  // Lectura única de props al montar: <ItemForm> se remonta al cambiar de item
  // (lo controla el <Switch> de la página), así que un valor inicial estático es
  // correcto y deliberado.
  // eslint-disable-next-line solid/reactivity
  const existingPlain = props.editing?.plain;
  const [type] = createSignal<ItemFormType>(
    existingPlain?.type === "note" ? "note" : (props.initialType ?? "login"),
  );
  const [name, setName] = createSignal(existingPlain?.name ?? "");
  const [username, setUsername] = createSignal(existingPlain?.fields.username ?? "");
  const [password, setPassword] = createSignal(existingPlain?.fields.password ?? "");
  const [url, setUrl] = createSignal(existingPlain?.fields.url ?? "");
  const [totp, setTotp] = createSignal(existingPlain?.totpSecret ?? "");
  const [notes, setNotes] = createSignal(existingPlain?.notes ?? "");
  const [showGenerator, setShowGenerator] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setError("");
    if (!name().trim()) {
      setError(t(msg`Name is required.`));
      return;
    }
    const fields: Record<string, string> = {};
    if (type() === "login") {
      if (username()) {
        fields.username = username();
      }
      if (password()) {
        fields.password = password();
      }
      if (url()) {
        fields.url = url();
      }
    }
    const plain: VaultItemPlain = {
      type: type(),
      name: name().trim(),
      fields,
      totpSecret: type() === "login" && totp() ? totp() : undefined,
      notes: notes() || undefined,
    };
    setBusy(true);
    try {
      await props.onSave(
        plain,
        props.editing ? { id: props.editing.id, rev: props.editing.rev } : undefined,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
      <div class="flex flex-col gap-1.5">
        <Label for="item-name">{t(msg`Name`)}</Label>
        <TextField
          id="item-name"
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          data-testid="item-name"
        />
      </div>

      <Show when={type() === "login"}>
        <div class="flex flex-col gap-1.5">
          <Label for="item-username">{t(msg`Username`)}</Label>
          <TextField
            id="item-username"
            autocomplete="off"
            value={username()}
            onInput={(e) => setUsername(e.currentTarget.value)}
            data-testid="item-username"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <Label for="item-password">{t(msg`Password`)}</Label>
          <div class="flex gap-2">
            <TextField
              id="item-password"
              type="text"
              autocomplete="off"
              class="font-mono"
              value={password()}
              onInput={(e) => setPassword(e.currentTarget.value)}
              data-testid="item-password"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t(msg`Open generator`)}
              onClick={() => setShowGenerator((v) => !v)}
              data-testid="item-open-generator"
            >
              <Wand2 class="h-4 w-4" />
            </Button>
          </div>
          <Show when={showGenerator()}>
            <PasswordGenerator
              onUse={(value) => {
                setPassword(value);
                setShowGenerator(false);
              }}
            />
          </Show>
        </div>

        <div class="flex flex-col gap-1.5">
          <Label for="item-url">{t(msg`Website`)}</Label>
          <TextField
            id="item-url"
            type="url"
            inputmode="url"
            value={url()}
            onInput={(e) => setUrl(e.currentTarget.value)}
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <Label for="item-totp" class="flex items-center gap-1.5">
            <KeyRound class="h-3.5 w-3.5" /> {t(msg`Authenticator secret (optional)`)}
          </Label>
          <TextField
            id="item-totp"
            autocomplete="off"
            class="font-mono"
            placeholder="JBSWY3DPEHPK3PXP"
            value={totp()}
            onInput={(e) => setTotp(e.currentTarget.value)}
          />
        </div>
      </Show>

      <div class="flex flex-col gap-1.5">
        <Label for="item-notes">{t(msg`Notes`)}</Label>
        <textarea
          id="item-notes"
          rows="4"
          class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          value={notes()}
          onInput={(e) => setNotes(e.currentTarget.value)}
          data-testid="item-notes"
        />
      </div>

      <Show when={error()}>
        <p class="text-sm text-destructive" role="alert">
          {error()}
        </p>
      </Show>

      <div class="flex gap-2">
        <Button type="submit" loading={busy()} data-testid="item-save">
          {t(msg`Save`)}
        </Button>
        <Button type="button" variant="ghost" onClick={() => props.onCancel()}>
          {t(msg`Cancel`)}
        </Button>
      </div>
    </form>
  );
}
